import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { DOC_CATEGORIES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [category, setCategory] = useState("Outro");
    const [processId, setProcessId] = useState("");
    const [uploading, setUploading] = useState(false);

    const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    useEffect(() => { load(); api.get("/processes").then((r) => setProcesses(r.data)).catch(() => {}); }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        fd.append("process_id", processId);
        setUploading(true);
        try {
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento enviado.");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar."); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const del = async (id) => { await api.delete(`/documents/${id}`); toast.success("Documento excluído."); load(); };
    const procNum = (pid) => processes.find((p) => p.id === pid)?.process_number;

    return (
        <div className="space-y-6" data-testid="documents-page">
            <PageHeader title="Documentos" subtitle="Central de documentos — anexe e relacione aos seus processos." />

            <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
                <div className="grid sm:grid-cols-3 gap-3 items-end">
                    <div>
                        <label className="text-sm font-semibold text-slate-700 mb-1 block">Categoria</label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-700 mb-1 block">Processo (opcional)</label>
                        <Select value={processId || "none"} onValueChange={(v) => setProcessId(v === "none" ? "" : v)}>
                            <SelectTrigger data-testid="doc-process"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                            <SelectContent><SelectItem value="none">Nenhum</SelectItem>{processes.map((p) => <SelectItem key={p.id} value={p.id}>{p.process_number}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <label className={`flex items-center justify-center gap-2 bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-5 py-2.5 font-semibold cursor-pointer transition-colors ${uploading ? "opacity-60" : ""}`} data-testid="doc-upload-button">
                        <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Enviar arquivo"}
                        <input type="file" className="hidden" onChange={onUpload} disabled={uploading} accept=".pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png" />
                    </label>
                </div>
                <p className="text-xs text-slate-400 mt-3">Formatos: PDF, DOCX, XLSX, CSV, JPG, PNG. Tamanho máximo: 15MB.</p>
            </div>

            {docs.length === 0 ? (
                <EmptyState icon={FileText} title="Nenhum documento" desc="Envie ART, alvarás, relatórios, mapas e outros arquivos." />
            ) : (
                <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b-2 border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                            <th className="py-3 px-4">Arquivo</th><th className="py-3 px-4">Categoria</th><th className="py-3 px-4">Processo</th><th className="py-3 px-4">Tamanho</th><th className="py-3 px-4"></th>
                        </tr></thead>
                        <tbody>
                            {docs.map((d) => (
                                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`doc-row-${d.id}`}>
                                    <td className="py-3 px-4 text-slate-800 font-medium">{d.filename}</td>
                                    <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{d.category}</span></td>
                                    <td className="py-3 px-4 font-mono text-petroleum">{procNum(d.process_id) || "—"}</td>
                                    <td className="py-3 px-4 text-slate-500 font-mono">{(d.size / 1024).toFixed(0)} KB</td>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-1 justify-end">
                                            <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-petroleum transition-colors" data-testid={`download-doc-${d.id}`}><Download className="w-4 h-4" /></a>
                                            <button onClick={() => del(d.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
